'use strict';
import template from './mx-generic-grid.component.html';
import { initFilterExpression, toCamelCase } from '../../../model/filterExpressionUtils';

export default {
	bindings: {
		type: '@', //type of grid, mandatory

		data: '<',

		columnDefinition: '@',
		columns: '<',

		enableRowSelection: '<',
		enableSelectionPicker: '<',
		externalSelectedItems: '<selectedItems', //one-way, because it is necessary not to change whole array from designer, and only his items

		currentItem: '=?',

		// configure preprocessors
		enablePaging: '<',
		enableSorting: '<',
		enableSearch: '<',
		enableColumnSearch: '<',
		enableAdvanceSearch: '<',

		//Edit mode
		inlineMode: '<',
		editMode: '<',

		//buttons
		addButtonHidden: '<disableAdd',
		createButtonHidden: '<disableCreate',
		removeButtonHidden: '<disableRemove',
		createButtonInSelectionHidden: '<createButtonInSelectionHidden',
		disableEdit: '<',
		deleteActionIds: '@',

		editDisableFieldName: '@disableEditFieldName',
		removeDisableFieldName: '@disableDeleteFieldName',
		hideItemFieldName: '@',

		showAddButton: '<', //obsolete attribute for dynamic-grid
		showRemoveButton: '<', //obsolete attribute for dynamic-grid

		clickAction: '@',
		dblClickAction: '@',

		//Appearance
		title: '@',
		fullHeight: '<',
		minHeight: '<',

		headerHidden: '<hideHeader',
		showActionsPanel: '<',
		exportButtonHidden: '<hideExport',
		enableDragAndDrop: '<enableDragNDrop',

		readOnly: '<',
		disabled: '<',
		required: '<',

		//objects grid
		entityClassName: '@',
		allowedTypes: '@',
		// filter: '<',
		// browseWhereClause: '<',

		objectId: '@', //for multifragment

		onlyTypedSelectSearch: '<',

		addItemTitle: '@',
		editItemTitle: '@',
		templateId: '@', // template of edit-form
		wizardId: '@',
		outerContext: '<',

		mxAtId: '@', // id for personalization

		parentObject: '<', //legacy attribute to support objects-grid-deprecated
	},
	require: {
		sidePanelTabBody: '?^mxSidePanelTabBody',
		serviceContext: '^mxServiceContext',
		mxForm: '?^mxForm',
		mxUiAccessibility: '?^mxUiAccessibility',
		parentViewController: '?^^mxView',
	},
	template: template,
	controller: MxGenericGridController,
	controllerAs: 'vm',
};

MxGenericGridController.$inject = [
	'$stateParams',
	'$q',
	'$scope',
	'$attrs',
	'$element',
	'mx.shell.NavigationService',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.ContentWidgetService',

	'mx.SolutionBuilderAgent.ObjectsGrid',
	'mx.SolutionBuilderAgent.RelationsGrid',
	'mx.SolutionBuilderAgent.ObjectsDeprecatedGrid',
	'mx.SolutionBuilderAgent.DynamicGrid',
	'mx.SolutionBuilderAgent.MultiFragmentGrid',
];

export function MxGenericGridController(
	$stateParams,
	$q,
	$scope,
	$attrs,
	$element,
	navigationService,
	i18n,
	contentWidgetService,

	ObjectsGrid,
	RelationsGrid,
	ObjectsDeprecatedGrid,
	DynamicGrid,
	MultiFragmentGrid
) {
	const vm = this;

	vm.$onInit = function () {
		const initFilter = (propertyName) => {
			const property = toCamelCase(propertyName);
			if (!vm.grid.hasOwnProperty(property)) {
				return;
			}

			vm.grid[property] = initFilterExpression({
				propertyName,
				controlMxAtId: vm.mxAtId,
				$scope,
				$attrs,
				$element,
				controller: vm,
				onChanged: (filter) => {
					vm.grid[property] = filter;
				},
			});
		};

		initFilter('filter');
		initFilter('browse-where-clause');

		getPersonalizationKey()
			.then(function (personalizationKey) {
				return vm.grid.init(personalizationKey);
			})
			.then(function () {
				vm.grid.executor.addSelectionHandler(selectionHandler);
				vm.grid.addCurrentRowChangeHandler(currentRowChangeHandler);
			})
			.then(function () {
				if (Array.isArray(vm.externalSelectedItems)) {
					setSelectedItems(vm.externalSelectedItems);
				}
			});
	};

	var destroyed = false;
	vm.$onChanges = function (changes) {
		if (destroyed) {
			return;
		}

		if (changes.type) {
			vm.grid = initGrid(vm.type, vm.personalizationKey);
			vm.grid.parentView = vm.sidePanelTabBody && vm.sidePanelTabBody.view;
			vm.grid.mxForm = vm.mxForm;
			vm.grid.mxUiAccessibility = vm.mxUiAccessibility;
			vm.grid.serviceContext = vm.serviceContext;
		}

		if (!vm.grid) {
			return;
		}

		if (changes.externalSelectedItems) {
			if (vm.grid.executor) {
				setSelectedItems(changes.externalSelectedItems.currentValue);
			}
		}

		Object.keys(changes).forEach(function (propertyName) {
			if (vm.grid.hasOwnProperty(propertyName)) {
				vm.grid[propertyName] = changes[propertyName].currentValue;
			}
		});

		vm.grid.onChanges(changes);
	};

	vm.$onDestroy = function () {
		destroyed = true;

		vm.grid.onDestroy();
	};

	function selectionHandler(data) {
		if (Array.isArray(vm.externalSelectedItems)) {
			//to avoid losing link to array from designer, don't change whole array, and just change its items
			vm.externalSelectedItems.splice.apply(vm.externalSelectedItems, [0, vm.externalSelectedItems.length].concat(data.allSelectedEntities));
		}
	}

	function setSelectedItems(selectedItems) {
		selectedItems = Array.isArray(selectedItems) ? selectedItems : [];

		return $q
			.all(
				selectedItems.map(function (item) {
					return vm.grid.executor.createRow(item);
				})
			)
			.then(function (selectedRows) {
				vm.grid.executor.changeSelection(selectedRows);
			});
	}

	function currentRowChangeHandler(row) {
		vm.currentItem = row && row.getEntity();
	}

	function initGrid(type) {
		var grid;
		switch (type) {
			case 'objects':
				grid = new ObjectsGrid();
				break;
			case 'objects-deprecated':
				grid = new ObjectsDeprecatedGrid();
				break;
			case 'relation':
				grid = new RelationsGrid();
				break;
			case 'dynamic':
				grid = new DynamicGrid();
				break;
			case 'multifragment':
				grid = new MultiFragmentGrid();
				break;
		}

		return grid;
	}

	function getWidgetId() {
		var view = vm.sidePanelTabBody && vm.sidePanelTabBody.view;
		if (view) {
			// grid is located in side-panel. Get id from view
			return $q.when(contentWidgetService.getWidgetIdFromView(view));
		} else {
			// grid is located in main-zone (ex. landing-page). Get id from $state or navigation
			//TODO: some mechanism is needed to get WidgetId of MainZone
			if ($stateParams.widgetId) {
				return $q.when($stateParams.widgetId);
			} else {
				var navigationId = ($stateParams.navigationId || '').toLowerCase();
				if (navigationId && navigationId !== 'notset') {
					return navigationService.getNavigationItem(navigationId).then(function (navigationItem) {
						return navigationItem && navigationItem.contentWidgetId;
					});
				} else {
					return $q.when();
				}
			}
		}
	}

	function getPersonalizationKey() {
		return getWidgetId().then(function (widgetId) {
			vm.personalizationKey = widgetId + vm.mxAtId;
			return vm.personalizationKey;
		});
	}

	return vm;
}
